import { Search, SlidersHorizontal, X } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { cn } from '../../lib/utils';

function FilterField({ label, htmlFor, className, children }) {
  return (
    <div className={cn('flex min-w-[10rem] flex-1 flex-col gap-2', className)}>
      <Label htmlFor={htmlFor} className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </Label>
      {children}
    </div>
  );
}

function FilterSelect({ id, value, onValueChange, placeholder, options }) {
  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger id={id} className="h-10 w-full bg-white">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.id} value={option.id}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export default function ProgramFilters({
  programSearch,
  onProgramSearchChange,
  genderFilterId,
  onGenderFilterChange,
  genderOptions,
  languageFilterId,
  onLanguageFilterChange,
  languageOptions,
  programFilterId,
  onProgramFilterChange,
  programOptions,
  collegeFilterId,
  onCollegeFilterChange,
  collegeOptions,
  resultCount,
  totalCount,
  onClearFilters,
  hasActiveFilters,
}) {
  return (
    <aside
      className="rounded-2xl border border-slate-200/80 bg-gradient-to-br from-slate-50 via-white to-sky-50/40 p-4 shadow-sm sm:p-5"
      aria-label="Program filters"
    >
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-200/80 pb-4">
        <div className="flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#0084BD]/10 text-[#0084BD]">
            <SlidersHorizontal className="h-4 w-4" aria-hidden />
          </span>
          <div>
            <h3 className="text-sm font-bold text-slate-900">Refine results</h3>
            <p className="text-xs text-slate-500">
              {resultCount === totalCount
                ? `${totalCount} programs`
                : `${resultCount} of ${totalCount} programs`}
            </p>
          </div>
        </div>
        {hasActiveFilters && (
          <button
            type="button"
            onClick={onClearFilters}
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold text-slate-600 ring-1 ring-slate-200 transition hover:bg-white hover:text-[#0084BD] hover:ring-[#0084BD]/30"
          >
            <X className="h-3.5 w-3.5" aria-hidden />
            Clear filters
          </button>
        )}
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <FilterField
          label="Search program"
          htmlFor="program-search"
          className="w-full sm:col-span-2 sm:max-w-none lg:col-span-4"
        >
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
              aria-hidden
            />
            <Input
              id="program-search"
              type="search"
              value={programSearch}
              onChange={(e) => onProgramSearchChange(e.target.value)}
              placeholder="Program name, college, or discipline…"
              className="h-10 bg-white pl-9"
            />
          </div>
        </FilterField>

        <FilterField label="Program type" htmlFor="program-filter">
          <FilterSelect
            id="program-filter"
            value={programFilterId}
            onValueChange={onProgramFilterChange}
            placeholder="Select program type"
            options={programOptions}
          />
        </FilterField>

        <FilterField label="College" htmlFor="college-filter">
          <FilterSelect
            id="college-filter"
            value={collegeFilterId}
            onValueChange={onCollegeFilterChange}
            placeholder="Select college"
            options={collegeOptions}
          />
        </FilterField>

        <FilterField label="Gender" htmlFor="gender-filter">
          <FilterSelect
            id="gender-filter"
            value={genderFilterId}
            onValueChange={onGenderFilterChange}
            placeholder="Select gender"
            options={genderOptions}
          />
        </FilterField>

        <FilterField label="Language" htmlFor="language-filter">
          <FilterSelect
            id="language-filter"
            value={languageFilterId}
            onValueChange={onLanguageFilterChange}
            placeholder="Select language"
            options={languageOptions}
          />
        </FilterField>
      </div>
    </aside>
  );
}
